import React, { FC, ReactNode } from 'react';
import CatalogueNavigation from '../../shop/catalogue/CatalogueNavigation';
import SelectionListNavigation from '../navigation/SelectionListNavigation';
import ReserveDetailNavigation from '../navigation/ReserveDetailNavigation';
import CartNavigation from '../navigation/CartNavigation';
import { ShopProductDetailNavigation } from '../navigation/ProductDetailNavigation';
import { BundleDetailNavigation } from '../navigation/BundleDetailNavigation';
import UnlimitedNavigation from '../navigation/UnlimitedNavigation';
import SearchNavigation from '../search/SearchNavigation';
import { CartPreview } from '../../shop/cart/CartPreview';
import LocalContentNavigation from '../../yourborrowbox/navigation/LocalContentNavigation';
import CollectionNavigation from '../../yourborrowbox/navigation/CollectionNavigation';
import LibxNavigation from '../../yourborrowbox/navigation/LibxNavigation';
import ForeignLanguageNavigation from '../navigation/ForeignLanguageNavigation';
import LoteNavigation from '../navigation/LoteNavigation';
import LanguageLearningNavigation from '../navigation/LanguageLearningNavigation';
import MarketingNavigation from '../../yourborrowbox/navigation/MarketingNavigation';
import OrderAssistantNavigation from '../navigation/OrderAssistantNavigation';
import ReserveCheckoutNavigation from '../../shop/reserve/components/ReserveCheckoutNavigation';
import { SpocState } from '../../store/authentication/state';
import { NavigationLink } from '../navigation/NavigationLink';
import { UseCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import CollectionManagementNavigation from '../../yourborrowbox/navigation/CollectionManagementNavigation';
import MerchandisingNavigation from '../../yourborrowbox/navigation/MerchandisingNavigation';
import { useDisplayFilterSearchParam } from '../utils/useDisplayFilterSearchParam';
import { Route, Routes } from 'react-router-dom';
import { OrdersNavigationBreadcrumbs } from '../../shop/orders/OrdersNavigation';

export type ScrollHeaderProps = {
    readonly show: boolean;
    readonly spoc: SpocState;
    readonly onOpenSearchBar: () => void;
    readonly searchbar: ReactNode;
    readonly cartSummaryQuery: UseCartSummaryQuery;
};

export const ScrollHeader: FC<ScrollHeaderProps> = ({ show, spoc, searchbar, onOpenSearchBar, cartSummaryQuery }) => {
    const { toggleDisplayFilter } = useDisplayFilterSearchParam();

    return (
        <div
            className="header-sticky header-container"
            data-displayname="ScrollHeader"
            style={{ display: !show ? 'none' : undefined }}
        >
            <Routes>
                <Route
                    path="/catalogue/:loanFormat/products/*"
                    element={
                        <ul className="main-nav main-nav--sticky">
                            <li>
                                <a onClick={toggleDisplayFilter} role="button" className="filter-toggle">
                                    <i className="material-icons bigger">list</i>
                                </a>
                            </li>
                        </ul>
                    }
                />
                <Route
                    path="/shop/:shopLanguage/:loanFormat/products/*"
                    element={
                        <ul className="main-nav main-nav--sticky">
                            <li>
                                <a onClick={toggleDisplayFilter} role="button" className="filter-toggle">
                                    <i className="material-icons bigger">list</i>
                                </a>
                            </li>
                        </ul>
                    }
                />
                <Route
                    path="/catalogue/*"
                    element={
                        <ul className="main-nav main-nav--sticky">
                            <li>
                                <NavigationLink to="/catalogue">Shop</NavigationLink>
                            </li>
                        </ul>
                    }
                />
                <Route
                    path="/shop/*"
                    element={
                        <ul className="main-nav main-nav--sticky">
                            <li>
                                <NavigationLink to="/shop">Shop</NavigationLink>
                            </li>
                        </ul>
                    }
                />
            </Routes>
            <div className="header-sticky-nav-right fl-right display-flex">
                <div className="search-icon">
                    {searchbar}
                    <i className="material-icons icn-search" onClick={onOpenSearchBar}>
                        search
                    </i>
                </div>
                <CartPreview cartSummaryQuery={cartSummaryQuery} />
            </div>
            <Routes>
                {/* TODO [multi-language-shop] add missing shop routes */}
                <Route path="/assistant/reserve/:reserveListId" element={<ReserveDetailNavigation isSticky={true} />} />
                <Route
                    path="/assistant/reserve/:reserveListId/checkout/*"
                    element={<ReserveCheckoutNavigation isSticky={true} />}
                />
                <Route path="/assistant/reserves/*" element={<OrderAssistantNavigation isSticky={true} />} />
                <Route path="/bundles/:bundleId" element={<BundleDetailNavigation isSticky={true} />} />
                <Route path="/cart/*" element={<CartNavigation isSticky={true} isCheckout={false} />} />
                <Route path="/catalogue/*" element={<CatalogueNavigation isSticky={true} />} />
                <Route path="/checkout/*" element={<CartNavigation isSticky={true} isCheckout={true} />} />
                <Route path="/collection/*" element={<CollectionNavigation isSticky={true} />} />
                <Route path="/language/lote/:languageId/featured/*" element={<LoteNavigation isSticky={true} />} />
                <Route path="/language/lote/:languageId/newlyadded/*" element={<LoteNavigation isSticky={true} />} />
                <Route path="/language/lote/:languageId/genres/*" element={<LoteNavigation isSticky={true} />} />
                <Route path="/language/lote/:languageId/products/*" element={<LoteNavigation isSticky={true} />} />
                <Route
                    path="/language/languagelearning/:languageId/featured/*"
                    element={<LanguageLearningNavigation isSticky={true} />}
                />
                <Route
                    path="/language/languagelearning/:languageId/newlyadded/*"
                    element={<LanguageLearningNavigation isSticky={true} />}
                />
                <Route
                    path="/language/languagelearning/:languageId/products/*"
                    element={<LanguageLearningNavigation isSticky={true} />}
                />
                <Route path="/language/*" element={<ForeignLanguageNavigation isSticky={true} />} />
                <Route path="/libxchange/*" element={<LibxNavigation isSticky={true} />} />
                <Route path="/localcontent/*" element={<LocalContentNavigation isSticky={true} />} />
                <Route path="/orderlists/:id/*" element={<SelectionListNavigation isSticky={true} spoc={spoc} />} />
                <Route path="/orders/:orderId?" element={<OrdersNavigationBreadcrumbs isSticky={true} />} />
                <Route path="/products/:productId" element={<ShopProductDetailNavigation isSticky={true} />} />
                <Route path="/search/*" element={<SearchNavigation isSticky={true} />} />
                <Route path="/unlimited/*" element={<UnlimitedNavigation isSticky={true} />} />
                <Route path="/marketing/*" element={<MarketingNavigation isSticky={true} />} />
                <Route
                    path={'/collectionmgmt/collection/*'}
                    element={
                        <CollectionManagementNavigation
                            isSticky={true}
                            showLoanFormatSelector={true}
                            showAgeCategorySelector={true}
                        />
                    }
                />
                <Route
                    path={'/collectionmgmt/campaigns/*'}
                    element={
                        <CollectionManagementNavigation
                            isSticky={true}
                            showLoanFormatSelector={true}
                            showAgeCategorySelector={true}
                        />
                    }
                />
                <Route
                    path={'/collectionmgmt/reserves/*'}
                    element={
                        <CollectionManagementNavigation
                            isSticky={true}
                            showLoanFormatSelector={false}
                            showAgeCategorySelector={false}
                        />
                    }
                />
                <Route
                    path={'/collectionmgmt/expiries/*'}
                    element={
                        <CollectionManagementNavigation
                            isSticky={true}
                            showLoanFormatSelector={false}
                            showAgeCategorySelector={false}
                        />
                    }
                />
                <Route
                    path={'/collectionmgmt/subscriptions/*'}
                    element={
                        <CollectionManagementNavigation
                            isSticky={true}
                            showLoanFormatSelector={false}
                            showAgeCategorySelector={false}
                        />
                    }
                />
                <Route path="/merchandising/*" element={<MerchandisingNavigation isSticky={true} />} />
            </Routes>
        </div>
    );
};
